<script lang="ts">
    import Modal from "../../../components/Modal.svelte"
    import Cta from "../../../components/CTA.svelte"
    import Icon from "../../../components/Icon.svelte"
    import { mdiCloudUploadOutline, mdiDeleteOutline, mdiRefresh } from "@mdi/js"

    import { isMobile, newNotification } from "../../../store"
    import LoadingWrapper from "../../widgets/LoadingWrapper.svelte"

    import { openCameraInput, openFileInput } from "../helper"
    import { readInput } from "./helper"
    import { apiBaseURL } from "../../../../config"
    import ImageMetaData from "../../widgets/TaskImages/ImageMetaData.svelte"
    import { deleteDBEntry } from "../../../../api"
    import { createEventDispatcher } from "svelte"
    const dispatch = createEventDispatcher()
    export let imagesData: Media[] = []
    export let showImageUploadModal: boolean
    export let taskId: string

    let sessionImages: Media[] = []
    let modalStates = {
        isUploading: false,
    }
    export async function handleFileChange(event: Event) {
        const input = event.target as HTMLInputElement
        modalStates.isUploading = true
        newNotification({ html: "Bilder werden hochgeladen...", class: "info" })

        if (input.files) {
            const filePromises = await readInput(input, taskId)
            try {
                const images = await Promise.all(filePromises)
                sessionImages.push(...images)
                sessionImages = [...sessionImages]
                imagesData = [...imagesData, ...images]
            } catch (error) {
                console.error("Error uploading files:", error)
                newNotification({ html: "Fehler beim Hochladen der Dateien", class: "error" })
            } finally {
                modalStates.isUploading = false
            }
        }
    }

    export async function replaceImage(index: number) {
        loadingIndex = index
        await deleteDBEntry("media", sessionImages[index].id)

        openFileInput(async (event: Event) => {
            const input = event.target as HTMLInputElement
            const newImage = await readInput(input)
            const img = await Promise.all(newImage)
            updateImageLists(img, index)

            newNotification({ html: "Bild wurde ersetzt", class: "success" })
            loadingIndex = -1
        })
    }

    function updateImageLists(newImage: Media[], index: number) {
        const imgDataIndx = imagesData.findIndex((img) => img.id === sessionImages[index].id)
        imagesData[imgDataIndx] = newImage[0]
        imagesData = [...imagesData]
        sessionImages[index] = newImage[0]
        sessionImages = [...sessionImages]
    }
    const deleteImage = async (index: number) => {
        loadingIndex = index
        await deleteDBEntry("media", sessionImages[index].id)
        newNotification({
            html: "Bild wurde gelöscht",
            class: "success",
        })
        const imgDataIndx = imagesData.findIndex((img) => img.id === sessionImages[index].id)
        imagesData.splice(imgDataIndx, 1) // Remove the image from the imagesData array
        imagesData = [...imagesData] // Update imagesData
        sessionImages.splice(index, 1) // Remove the image from the session array
        sessionImages = [...sessionImages] // Update sessionImages

        loadingIndex = -1
    }

    let gotTriggered = false
    $: if (showImageUploadModal && !gotTriggered) {
        gotTriggered = true
        openCameraInput(handleFileChange)
    }
    let loadingIndex = -1
</script>

<Modal
    bind:show="{showImageUploadModal}"
    size="md"
    on:close="{() => {
        dispatch('forceUpdate')
        showImageUploadModal = false
    }}"
>
    <h3
        slot="header"
        class="label"
    >
        <Icon
            path="{mdiCloudUploadOutline}"
            size="1.2rem"
            color="#6C757D"
        />
        <p>Bilder hochladen oder aufnehmen</p>
    </h3>

    <p class="content-modal">
        Bitte wählen Sie die Bilder aus, die Sie hochladen oder aufnehmen möchten. Sie können mehrere Bilder
        gleichzeitig auswählen. Auf dem Computer müssen Sie die Bilder aus Ihrer Bibliothek auswählen. Auf einem
        Smartphone können Sie die Kamera verwenden, um Bilder aufzunehmen.
    </p>

    <div class="image-preview upload">
        {#each sessionImages as image, index}
            <LoadingWrapper active="{loadingIndex === index}">
                <div class="image-container">
                    {#if image}
                        <div class="img-wrapper">
                            {#key loadingIndex}
                                <img
                                    src="{`${apiBaseURL}media/${image.id}/${image?.file?.src}`}"
                                    alt="Image Preview"
                                    class="image"
                                    aria-hidden="true"
                                />
                            {/key}
                            <div class="actionRow">
                                <button
                                    class="replace-button"
                                    on:click="{() => replaceImage(index)}"
                                >
                                    <Icon
                                        path="{mdiRefresh}"
                                        size="1rem"
                                    />
                                </button>
                                <button
                                    class="delete-button"
                                    on:click="{() => deleteImage(index)}"
                                >
                                    <Icon
                                        path="{mdiDeleteOutline}"
                                        size="1rem"
                                    />
                                </button>
                            </div>
                        </div>
                        <ImageMetaData image="{image}" />
                    {/if}
                </div></LoadingWrapper
            >
        {/each}
    </div>

    <div slot="footer">
        <LoadingWrapper
            active="{modalStates.isUploading}"
            styles="display: flex; gap: {$isMobile ? '0.6rem' : '1.2rem'};"
        >
            <Cta
                type="primaryButton"
                icon="solar_folder-with-files-outline"
                content="{$isMobile ? 'Auswählen' : 'Bilder aus Bibliothek auswählen'}"
                on:click="{() => openFileInput(handleFileChange)}"
            />
            <Cta
                icon="solar_camera-outline"
                type="tertiaryButton"
                content="{$isMobile ? 'Aufnehmen' : 'Bilder Aufnehmen '}"
                on:click="{() => openCameraInput(handleFileChange)}"
            />
            <Cta
                type="tertiaryButton"
                icon="solar_close-circle-outline"
                content="{$isMobile ? '' : 'Schließen'}"
                on:click="{() => {
                    dispatch('forceUpdate')
                    showImageUploadModal = false
                }}"
            />
        </LoadingWrapper>
    </div>
</Modal>

<style lang="less">
    @import "../../../../../assets/variables.less";

    h3.label {
        display: flex;
        gap: 0.6rem;
        align-items: center;
    }

    :global .upload.image-preview {
        display: flex;
        flex-wrap: wrap;
        gap: 1rem;
        & > div {
            width: fit-content !important;
        }
    }
    .img-wrapper {
        width: 100%;
        aspect-ratio: 1/1;

        max-width: 300px;
        @media @mobile {
            max-width: 100%;
        }

        img {
            width: 100%;
            aspect-ratio: 1/1;
            object-fit: cover;
            border-radius: 12px;
        }
        @media @mobile {
            width: min(200px, 40vw);
            min-width: 100%;
            height: 100%;
            aspect-ratio: unset;
            img {
                aspect-ratio: unset;
                height: 100%;
            }
        }
        position: relative;

        .actionRow {
            display: flex;
            justify-content: space-between;
            align-items: center;
            position: absolute;
            top: 0.6rem;
            left: 0.6rem;
            right: 0.6rem;
            button {
                display: flex;
                justify-content: center;
                align-items: center;
                width: 1.6rem;
                height: 1.6rem;
                border-radius: 48px;
                background: var(--text-100);
                color: white;
            }
        }
    }
</style>
